import {
  Component,
  effect,
  inject,
  signal,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ImageStore } from '../../../services/api/image/image-store.service';
import { ButtonComponent } from '../../../components/feedback/button/button.component';
import { LoadingAnimationComponent } from '../../../components/feedback/loading-animation/loading-animation.component';
import { DATE_FORMAT_LONG, IImage, ImageId } from '@web/dtos';
import { IImageForm } from '../../../entities/image-entity';
import { ImageFormsFactoryService } from '../../../factory/image/image-forms-factory.service';

import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import {
  ITableSortParams,
  TableComponent,
  TableSortOrder,
  toSortOrder,
} from '../../../components/data-display/table/table.component';
import { TableHeaderComponent } from '../../../components/data-display/table/table-header/table-header.component';
import { CheckboxComponent } from '../../../components/data-input/form-inputs/checkbox/checkbox.component';
import { map, Observable, Subscription } from 'rxjs';
import { IDialogData } from '../../../components/dialogs/dialog';
import { ImagePreviewDialogComponent } from '../../../components/dialogs/image-preview/image-preview.component';
import { ResetChangesDialogComponent } from '../../../components/dialogs/reset-changes-dialog/reset-changes-dialog.component';
import { TableCellComponent } from '../../../components/data-display/table/table-cell/table-cell.component';
import { DialogService } from '../../../services/support/dialog/dialog.service';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import { FormTracking } from '../form-tracking/form-tracking.component';
import { SearchBarComponent } from '../../../components/data-input/form-inputs/search-bar/search-bar.component';

interface INewImagesForm {
  images: FormControl<File[]>;
}

interface ICheckBoxForm {
  checkbox: FormControl<boolean>;
}

interface ISearchForm {
  search: FormControl<string | null>;
  dateFrom: FormControl<Date | null>;
  dateTo: FormControl<Date | null>;
  showPreview: FormControl<boolean>;
  showFileName: FormControl<boolean>;
  showCreatedAt: FormControl<boolean>;
}

@Component({
  selector: 'aw-images-page',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    LoadingAnimationComponent,
    AppendTestIdPipe,
    TableComponent,
    TableHeaderComponent,
    CheckboxComponent,
    TableCellComponent,
    SearchBarComponent,
  ],
  templateUrl: './image-page.component.html',
  styles: ``,
})
export class ImagePageComponent extends FormTracking<INewImagesForm> {
  readonly imageStore: ImageStore = inject(ImageStore);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private dragCounter: number = 0;
  private pendingImagePreviewUrl: string | null = null;
  private readonly imageFormFactory: ImageFormsFactoryService = inject(
    ImageFormsFactoryService
  );
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  protected showOverlay: boolean = false;
  public readonly imagesForm: FormArray<FormGroup<IImageForm>> =
    this.formBuilder.nonNullable.array<FormGroup<IImageForm>>([]);
  public readonly componentSubscriptions: Subscription = new Subscription();
  public allImages: IImage[] = this.imageStore.images();
  public toBeDeletedImages: WritableSignal<(IImage | FormGroup<IImageForm>)[]> =
    signal([]);

  public filteredImages: IImage[] = this.allImages;
  protected readonly DATE_FORMAT: string = DATE_FORMAT_LONG;
  public newImagesForm: FormGroup<INewImagesForm> = this.formBuilder.group({
    images: this.formBuilder.nonNullable.control<File[]>([]),
  });
  public newImage: IImage | null = null;
  public checkboxAllImages: FormGroup<ICheckBoxForm> = new FormGroup({
    checkbox: this.formBuilder.nonNullable.control<boolean>(false),
  });
  filename: FormControl = new FormControl();
  public readonly filterForm: FormGroup<ISearchForm> = new FormGroup({
    search: this.formBuilder.control<string | null>(null),
    dateFrom: this.formBuilder.control<Date | null>(null),
    dateTo: this.formBuilder.control<Date | null>(null),
    showPreview: this.formBuilder.nonNullable.control(true),
    showFileName: this.formBuilder.nonNullable.control(true),
    showCreatedAt: this.formBuilder.nonNullable.control(true),
  });
  constructor() {
    super();
    effect((): void => {
      this.allImages = this.imageStore.images();
      this.filteredImages = this.filterImages(
        this.allImages,
        this.toTextFilterValue(this.filterForm.getRawValue())
      );
    });

    effect(() => {
      const images: IImage[] = this.imageStore.images();
      this.imagesForm.clear();
      this.initFormTracking(this.newImagesForm);
      images?.forEach(image => {
        this.imagesForm.push(
          this.imageFormFactory.buildNormalImageFormGroup(image)
        );
      });
    });

    effect(() => {
      if (this.toBeDeletedImages()) {
        this.filteredImages = this.filterImages(
          this.allImages,
          this.toTextFilterValue(this.filterForm.value as any)
        );
      }
    });

    this.checkboxAllImages
      .get('checkbox')
      ?.valueChanges.subscribe((checked: boolean | null) => {
        if (checked === true || checked === false) {
          this.imagesForm.controls.forEach((control: FormGroup<IImageForm>) => {
            control.get('isChecked')?.setValue(checked, { emitEvent: false });
          });
        }
      });

    this.componentSubscriptions.add(
      this.imagesForm.valueChanges.subscribe(() => {
        const allChecked = this.imagesForm.controls.some(
          (control: FormGroup<IImageForm>) =>
            control.get('isChecked')?.value === true
        );

        this.checkboxAllImages.controls.checkbox.setValue(allChecked, {
          emitEvent: false,
        });
      })
    );

    this.componentSubscriptions.add(
      this.filterForm.valueChanges.subscribe(value => {
        this.filteredImages = this.filterImages(
          this.allImages,
          this.toTextFilterValue(value as any)
        );
      })
    );

    window.onbeforeunload = (event: BeforeUnloadEvent) => {
      if (this.hasChanges) {
        event.preventDefault();
      }
    };
  }

  public get hiddenColumns(): string[] {
    const hidden: string[] = [];

    if (!this.filterForm.controls.showPreview.value) {
      hidden.push('thumbnail');
    }

    if (!this.filterForm.controls.showFileName.value) {
      hidden.push('fileName');
    }

    if (!this.filterForm.controls.showCreatedAt.value) {
      hidden.push('createdAt');
    }

    return hidden;
  }

  get hasChanges(): boolean {
    return (
      this.hasUnsavedChanges(this.newImagesForm) ||
      this.toBeDeletedImages().length > 0
    );
  }
  public getPreviewPath(image: IImage): string {
    if (image.id) {
      return this.imageStore.getThumbnailPath(image.id);
    }
    return this.pendingImagePreviewUrl ?? '';
  }

  save(): void {
    this.toBeDeletedImages().forEach(
      (image: IImage | FormGroup<IImageForm>) => {
        const imageId: ImageId | undefined | null = (
          image as FormGroup<IImageForm>
        ).controls
          ? (image as FormGroup<IImageForm>).value.id
          : (image as IImage).id;
        if (imageId) {
          this.deleteImage(imageId);
        } else {
          this.newImage = null;
        }
      }
    );

    const files: File[] | null = this.newImagesForm.controls.images.value;

    if (files && files.length > 0 && !this.getToBeDeletedIds().includes(null)) {
      const formData = new FormData();
      for (const file of files) {
        formData.append('newImages', file, file.name);
      }
      const formDataImages: IImage[] = this.imageStore
        .images()
        .filter((image: IImage) => image.id !== undefined);
      formData.append('images', JSON.stringify(formDataImages));
      this.newImagesForm.controls.images.setValue([]);
      this.filename.reset();
      this.newImage = null;
      this.imageStore.upload(formData);
      this.toBeDeletedImages.set([]);
      return;
    }
    this.imageStore.save(
      this.filterToBeDeletedImages(this.imageStore.images())
    );
    this.toBeDeletedImages.set([]);
  }

  markImageForDeletion(image: IImage): void {
    if (!image.id) {
      this.newImage = null;
    }
    if (this.hasUsage(image)) {
      return;
    }
    const markedForDeletion: FormGroup<IImageForm> | undefined =
      this.imagesForm.controls.find(
        (control: FormGroup<IImageForm>) =>
          control.value.id === image.id || (!image.id && !control.value.id)
      );
    if (markedForDeletion) {
      this.toBeDeletedImages.set(
        this.toBeDeletedImages().concat(markedForDeletion)
      );
    }
  }

  deleteImage(imageId: ImageId): void {
    this.imageStore.delete(imageId);
  }

  public handleAbortChangesDialog(): Observable<boolean> {
    const dialogRef = this.dialogService.openDialog<IDialogData>(
      ResetChangesDialogComponent,
      {
        headline: this.cmsTextProvider.shared.revertChanges,
        width: '600px',
        icon: 'warning',
        iconBackgroundColor: '#eed202',
      }
    );
    dialogRef.closed.subscribe(result => {
      if (result != undefined && result.submit) {
        this.imageStore.reload();
      }
    });
    return dialogRef.closed.pipe(map(result => result?.submit ?? false));
  }

  public imageNameFilter: ITableSortParams<IImage>['compare'] = (
    a: IImage,
    b: IImage,
    order: TableSortOrder
  ): number => {
    const sortDirection = toSortOrder(order);
    return a.filename.toLowerCase() > b.filename.toLowerCase()
      ? 1 * sortDirection
      : -1 * sortDirection;
  };

  public imageCreationDateFilter: ITableSortParams<IImage>['compare'] = (
    a: IImage,
    b: IImage,
    order: TableSortOrder
  ): number => {
    const sortDirection = toSortOrder(order);
    return a.createdAt > b.createdAt ? 1 * sortDirection : -1 * sortDirection;
  };
  public imageUsageFilter: ITableSortParams<IImage>['compare'] = (
    a: IImage,
    b: IImage,
    order: TableSortOrder
  ): number => {
    const sortDirection = toSortOrder(order);
    if (a.usage == null && b.usage == null) {
      return 0;
    }
    if (a.usage == null) {
      return -1 * sortDirection;
    }
    if (b.usage == null) {
      return 1 * sortDirection;
    }
    return a.usage > b.usage ? 1 * sortDirection : -1 * sortDirection;
  };

  public filterImages(images: IImage[], filter: any): IImage[] {
    return this.filterToBeDeletedImages(
      (this.newImage ? ([this.newImage] as IImage[]) : [])
        .concat(images)
        .filter((image: IImage): boolean => {
          const matchesSearch: boolean = this.searchIncludesSubstring(
            image,
            filter.search
          );
          const matchesDateRange = this.isWithinDateRange(
            image,
            filter.dateFrom,
            filter.dateTo
          );
          return matchesSearch && matchesDateRange;
        })
        .sort((a: IImage, b: IImage): number => {
          if (a.createdAt > b.createdAt) {
            return -1;
          } else if (a.createdAt < b.createdAt) {
            return 1;
          } else {
            return 0;
          }
        })
    );
  }
  private isWithinDateRange(
    image: IImage,
    dateFrom: Date | null,
    dateTo: Date | null
  ): boolean {
    const createdAt = new Date(image.createdAt);
    if (Number.isNaN(createdAt.getTime())) {
      return false;
    }

    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      if (createdAt < from) {
        return false;
      }
    }

    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      if (createdAt > to) {
        return false;
      }
    }

    return true;
  }

  private filterToBeDeletedImages(images: IImage[]): IImage[] {
    return images.filter((image: IImage) => {
      const ids: (ImageId | null | undefined)[] = this.getToBeDeletedIds();
      return !ids.includes(image.id);
    });
  }

  private getToBeDeletedIds(): (ImageId | null | undefined)[] {
    return this.toBeDeletedImages().map(
      (markedImage: IImage | FormGroup<IImageForm>) =>
        (markedImage as IImage).id ||
        (markedImage as FormGroup<IImageForm>)?.value?.id
    );
  }

  private searchIncludesSubstring(
    subscription: IImage,
    subStringFilter: string | null | undefined
  ): boolean {
    if (!subStringFilter) {
      return true;
    }

    const filter: string = subStringFilter.toLowerCase();

    return [subscription.filename].some((field: string): boolean =>
      field?.toLowerCase().includes(filter)
    );
  }

  private toTextFilterValue(value: any): any {
    const compensateNull = <T>(value: T) => (value === 'null' ? null : value);

    return {
      search: compensateNull(value.search ?? null),
      dateFrom: compensateNull(value.dateFrom ?? null),
      dateTo: compensateNull(value.dateTo ?? null),
    };
  }

  public selectFilesEvent($event: Event): void {
    const target = $event.target as HTMLInputElement;
    const files = target.files as FileList;
    this.selectFiles(files.length > 0 ? [files.item(0) as File] : []);
  }

  public selectFiles(files: File[]): void {
    if (files && files.length > 0) {
      if (this.pendingImagePreviewUrl) {
        URL.revokeObjectURL(this.pendingImagePreviewUrl);
      }
      this.pendingImagePreviewUrl = URL.createObjectURL(files[0]);
      this.newImagesForm.controls.images.setValue(files);
      for (const file of files) {
        const lastDot = file.name.lastIndexOf('.');
        const filename = lastDot > 0 ? file.name.slice(0, lastDot) : file.name;
        const fileExtension =
          lastDot > 0 ? file.name.slice(lastDot + 1).toLowerCase() : '';

        const image: IImage = {
          filename,
          fileExtension,
          createdAt: new Date(),
        };
        if (
          this.filteredImages.length > 0 &&
          this.filteredImages[this.filteredImages.length - 1].id === undefined
        ) {
          this.filteredImages.pop();
          this.imagesForm.controls.pop();
        }
        this.newImage = image;

        this.filteredImages = this.filterImages(
          this.allImages,
          this.toTextFilterValue(this.filterForm.value as any)
        );
        this.imagesForm.push(
          this.imageFormFactory.buildNormalImageFormGroup(image)
        );
      }
      this.save();
    }
  }

  protected openImageInDialog(image: IImage): void {
    this.dialogService.openDialog<{ image: IImage }>(
      ImagePreviewDialogComponent,
      {
        headline: '',
        width: '1000px',
        icon: '',
        iconBackgroundColor: '',
        fontSize: 32,
        image: image,
      }
    );
  }

  public getUsage(image: IImage): string {
    return image.usage ?? '';
  }

  public hasUsage(image: IImage): boolean {
    return !!image.usage?.trim();
  }

  public indicateFormGroup(data: any): FormGroup<IImageForm> {
    const group = this.imagesForm.controls.find(group => {
      if (data.id === undefined) {
        return (
          group.get('filename')?.value === data.filename &&
          group.get('createdAt')?.value === data.createdAt
        );
      }
      return group.get('id')?.value === data.id;
    });
    if (!group) {
      throw new Error(`FormGroup for image with id "${data.id}" not found`);
    }

    return group as FormGroup<IImageForm>;
  }
  private getCheckedDeletableForms(): FormGroup<IImageForm>[] {
    return this.imagesForm.controls.filter((control: FormGroup<IImageForm>) => {
      const checked = control.controls.isChecked.value === true;
      const image = control.getRawValue() as IImage;
      return checked && !this.hasUsage(image);
    });
  }

  public anyCheckboxIsChecked(): boolean {
    return this.getCheckedDeletableForms().length === 0;
  }

  public markCheckedImagesForDeletion(): void {
    const markedForDeletion = this.getCheckedDeletableForms();

    if (markedForDeletion.find(i => !i.value.id)) {
      this.newImage = null;
    }

    this.toBeDeletedImages.set(
      this.toBeDeletedImages().concat(markedForDeletion)
    );
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    if (this.hasFiles(event)) {
      this.dragCounter++;
      this.showOverlay = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.showOverlay = false;
      this.dragCounter = 0;
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.showOverlay = false;
    this.dragCounter = 0;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.selectFiles(Array.from(files));
    }
  }

  private hasFiles(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.items || []).some(
      item => item.kind === 'file'
    );
  }
  ngOnDestroy(): void {
    if (this.pendingImagePreviewUrl) {
      URL.revokeObjectURL(this.pendingImagePreviewUrl);
      this.pendingImagePreviewUrl = null;
    }
    this.componentSubscriptions.unsubscribe();
  }
}
